import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Button, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getAnomalyIcon } from 'farmx-web-ui';
import { hooks } from 'farmx-redux-core';
import {
  getTextForIrrigationState,
  getStateForIrrigationState,
  getActionText,
} from './recommendation';
import { StatusTag } from '../components/StatusTag';
import { useTracking } from '../../../helper/mixpanel';
import HelpButton from '../components/HelpButton';
import SoilStateLegendModal from './SoilStateLegendModal';
import { getStateForAnomalySeverity } from '../../../helper/anomalyHelper';
import { getHumanReadableAnomalyType, getRecommendationIdentifier } from '../../../helper/common';

const { useBlockNames } = hooks;

export function RecommendationListItem({
  selectedTab, recommendation, loadInitialTab, anomalies,
}) {
  const history = useHistory();
  const location = useLocation();
  const { push } = history;
  const { t } = useTranslation();
  const tracking = useTracking();
  const [showModal, setShowModal] = useState(false);
  const blockName = useBlockNames([Number(recommendation?.block)]);

  const anomaly = anomalies?.find((a) => a.anomaly.properties.id === recommendation.imageryAnomaly)
    ?.anomaly;

  const translatedDescription = t(recommendation.description);
  const onDetailsClick = useCallback(() => {
    push({
      pathname: `/recommendation/detail/${getRecommendationIdentifier(recommendation)}`,
      state: { selectedTab },
    });
    tracking.track('Clicked recommendation details', recommendation);
  }, [selectedTab, push, recommendation, tracking]);

  const openModal = useCallback((e) => {
    e.stopPropagation();
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const onActionButtonClick = useCallback((event) => {
    event.stopPropagation();
    push({
      pathname: `/recommendation/action/${recommendation.block}`,
      state: {
        from: location.pathname,
        selectedTab,
      },
    });
    tracking.track('Clicked recommendation action', recommendation);
  }, [selectedTab, location.pathname, push, recommendation, tracking]);

  const onAnomalyButtonClick = useCallback((event, anomalyId) => {
    event.stopPropagation();
    const anomalyData = anomalies.filter((d) => (d?.anomaly?.properties?.id === anomalyId));

    push({
      pathname: `/recommendation/anomaly/detail/${getRecommendationIdentifier(recommendation)}`,
      state: { selectedTab, anomaly: anomalyData },
    });
    tracking.track('Clicked anomaly details', anomalyData);
  }, [anomalies, push, recommendation, selectedTab, tracking]);

  const onAnomalyActionButtonClick = useCallback((event, anomalyId) => {
    event.stopPropagation();
    const anomalyData = anomalies.filter((d) => (d?.anomaly?.properties?.id === anomalyId));

    push({
      pathname: `/recommendation/anomaly/investigate/${recommendation.block}`,
      state: { from: location.pathname, selectedTab, anomaly: [anomalyData[0]?.anomaly] },
    });
    tracking.track('Clicked anomaly action', anomalyData);
  }, [anomalies, location.pathname, push, recommendation.block, selectedTab, tracking]);

  const { selectedTab: initialTab } = location.state ?? {};

  useEffect(() => {
    loadInitialTab(initialTab);
  }, [initialTab, loadInitialTab]);

  function renderAnomalyRecommendationListItem() {
    if (!anomaly) return null;
    return (
      <Card
        className="recommendation-list"
        key={recommendation.id}
        title={recommendation.blockName || blockName[Number(recommendation.block)]}
        extra={(
          <div className="flex-center column-gap-10">
            <StatusTag
              text={t(getHumanReadableAnomalyType(anomaly.properties.anomaly_type))}
              state={getStateForAnomalySeverity((anomaly.properties
                && anomaly.properties.anomaly_severity))}
              icon={getAnomalyIcon(anomaly.properties.anomaly_type)}
            />
          </div>
        )}
        actions={[
          <div className="recommendation-list-item-footer">
            {recommendation.action !== 'no_action' ? (
              <div className="action-container-primary">
                <Button
                  size="large"
                  block
                  type="primary"
                  onClick={onAnomalyActionButtonClick}
                >
                  {t(getActionText(recommendation.action))}
                </Button>
              </div>
            ) : null}
          </div>]}
        bordered
        onClick={(e) => onAnomalyButtonClick(e, anomaly.properties.id)}
      >
        <div className="recommendation-list-item-content">
          <Typography.Text
            className="recommendation-description"
          >
            {t(recommendation?.description || recommendation?.anomaly?.properties?.description)}
          </Typography.Text>
          <Typography.Text
            className="recommendation-description"
          >
            <span className="ant-btn-link">{` ${t('More...')}`}</span>
          </Typography.Text>
        </div>
      </Card>
    );
  }

  function renderIrrigationRecommendationListItem() {
    return (
      <Card
        className="recommendation-list"
        key={recommendation.id}
        title={recommendation.blockName || blockName[Number(recommendation.block)]}
        extra={(
          <div className="flex-center column-gap-10">
            <StatusTag
              text={getTextForIrrigationState(recommendation.state)}
              state={getStateForIrrigationState(recommendation.state)}
            />
            <HelpButton onClick={openModal} />
          </div>
        )}
        actions={[
          <div className="recommendation-list-item-footer">
            {recommendation.action !== 'no_action' ? (
              <div className="action-container-primary">
                <Button
                  size="large"
                  block
                  type="primary"
                  onClick={onActionButtonClick}
                >
                  {t(getActionText(recommendation.action))}
                </Button>
              </div>
            ) : null}
          </div>]}
        bordered
        onClick={onDetailsClick}
      >
        <div className="recommendation-list-item-content">
          <Typography.Text
            className="recommendation-description"
          >
            {translatedDescription}
          </Typography.Text>
          <Typography.Text
            className="recommendation-description"
          >
            <span className="ant-btn-link">{` ${t('More...')}`}</span>
          </Typography.Text>
        </div>
      </Card>
    );
  }

  return (
    <>
      {recommendation.type === 'irrigation' && renderIrrigationRecommendationListItem()}
      {recommendation.type === 'imagery_anomaly' && renderAnomalyRecommendationListItem()}
      <SoilStateLegendModal
        visible={showModal}
        onCancel={closeModal}
      />
    </>
  );
}

RecommendationListItem.defaultProps = {
  selectedTab: 'new',
  loadInitialTab: () => { },
  anomalies: [],
};

RecommendationListItem.propTypes = {
  recommendation: PropTypes.arrayOf(PropTypes.any).isRequired,
  selectedTab: PropTypes.string,
  loadInitialTab: PropTypes.func,
  anomalies: PropTypes.arrayOf(PropTypes.any),
};
